import { Component } from "react";
import { AlertTriangle, RotateCcw, RefreshCw } from "lucide-react";
import api from "@/lib/api";
import { shouldReportError } from "@/lib/errorThrottle";

const Fallback = ({ onRetry }) => (
  <div className="min-h-screen flex items-center justify-center bg-[#13315C] p-6" data-testid="error-boundary">
    <div className="tf-grain absolute inset-0 opacity-20 pointer-events-none" />
    <div className="relative max-w-md w-full bg-white rounded-2xl p-8 text-center shadow-2xl">
      <div className="h-14 w-14 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-5">
        <AlertTriangle size={26} className="text-amber-500" />
      </div>
      <h1 className="font-display font-bold text-2xl text-[#13315C]">Algo se interrumpió</h1>
      <p className="mt-3 text-slate-600 text-sm leading-relaxed">
        Detectamos un problema y nuestro sistema de auto-recuperación ya lo registró.
        Puedes reintentar; tus datos están seguros.
      </p>
      <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
        <button
          onClick={onRetry}
          className="inline-flex items-center justify-center gap-2 bg-[#13315C] hover:bg-[#1C4076] text-white rounded-md h-11 px-5 font-semibold text-sm transition-colors"
          data-testid="error-retry-btn"
        >
          <RotateCcw size={16} /> Reintentar
        </button>
        <button
          onClick={() => window.location.reload()}
          className="inline-flex items-center justify-center gap-2 border border-slate-300 text-[#13315C] hover:bg-slate-50 rounded-md h-11 px-5 font-semibold text-sm transition-colors"
          data-testid="error-reload-btn"
        >
          <RefreshCw size={16} /> Recargar la página
        </button>
      </div>
    </div>
  </div>
);

export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, retries: 0 };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    console.error("ErrorBoundary capturó un error:", error, info?.componentStack);
    try {
      const payload = {
        message: `${error?.name || "Error"}: ${error?.message || error}`,
        stack: (info?.componentStack || error?.stack || "").slice(0, 4000),
        route: window.location.pathname,
        type: "error",
        meta: { kind: "react_boundary" },
      };
      if (shouldReportError(payload)) {
        api.post("/api/errors/log", payload).catch(() => {});
      }
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.debug("ErrorBoundary: fallo al reportar", err);
    }
  }

  handleRetry = () => this.setState((s) => ({ hasError: false, retries: s.retries + 1 }));

  render() {
    if (this.state.hasError) return <Fallback onRetry={this.handleRetry} />;
    return this.props.children;
  }
}
